// src/pages/ChatDocuments/ChatUploadMedia.tsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../shared/api/axios";
import { useAuth } from "../../../shared/contexts/AuthContext";
import type { User } from "../../../shared/chat/chatTypes";
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { ensureAllowedVideo } from '../../../shared/utils/videoGate';
import { uploadQueue } from "../../../shared/chat/uploadQueue";
import { useTranslation } from "react-i18next";


type NavState = {
  toUser?: User;
  openPicker?: boolean;

  // when returning from editors:
  previewFile?: File;
  editTargetId?: string;

  editedUrl?: string;           // blob: URL from editor
  editedMime?: string;          // optional
  previewFilename?: string;
};

type SelItem = {
  id: string;
  file: File;
  kind: "image" | "video";
  previewUrl: string;
  status: "ready" | "encrypting" | "uploading" | "sent" | "error";
  errorMsg?: string;
};

const ACCEPT = "image/*,video/*";

function guessKind(file: File): SelItem["kind"] | null {
  const type = file.type || '';
  const name = (file.name || '').toLowerCase();

  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('image/')) return 'image';

  // Safari often leaves type empty, so guess from extension
  if (/\.(mp4|mov|m4v|webm)$/i.test(name)) return 'video';
  if (/\.(jpe?g|png|gif|webp|heic|heif|avif)$/i.test(name)) return 'image';

  return null;
}

async function normalizeImageToJpeg(file: File): Promise<File> {
  // If it's already a safe, widely supported format, you *could* leave it.
  // But for maximum consistency you can just convert everything to JPEG.
  if (!file.type.startsWith('image/')) return file;

  // Optional: only convert "weird" ones; skip jpeg/png/webp:
  // const safeTypes = ['image/jpeg', 'image/png', 'image/webp'];
  // if (safeTypes.includes(file.type)) return file;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(fr.error || new Error('FileReader failed'));
    fr.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = (e) => reject(e || new Error('Image load failed'));
    i.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const blob: Blob | null = await new Promise(resolve =>
    canvas.toBlob(
      b => resolve(b),
      'image/jpeg',
      0.9,              // quality
    )
  );

  if (!blob) return file;

  const newName = file.name.replace(/\.\w+$/, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg' });
}


const ChatUploadMedia: React.FC = () => {
  const nav = useNavigate();
  const location = useLocation() as { state?: NavState };
  const locationState = location.state;
  const { state } = location;
  const { t } = useTranslation();
  const { toUser, openPicker, previewFile, editTargetId, previewFilename } = state || {};
  const { e2eeReady, user, token } = useAuth();
  const { mediaItems, addOrReplaceMediaItem, removeMediaItem, setMediaItems } = useMediaPreview();
  const [ttlAfterViewSec, setTtlAfterViewSec] = React.useState<number>(-1);

  const [items, setItems] = React.useState<SelItem[]>([]);
  const [sending, setSending] = React.useState(false);
  const hasMedia = items.length > 0; // 🔹 controls visibility of Presentation button

  const injectedPreviewRef = React.useRef<string | null>(null);

  const ttlOptions = React.useMemo(
    () => [
      { label: t('chat.upload.ttl.choose'),   value: -1 },
      { label: t('chat.upload.ttl.afterView'), value: 1 },
      { label: t('chat.upload.ttl.min1'),      value: 60 },
      { label: t('chat.upload.ttl.min5'),      value: 5 * 60 },
      { label: t('chat.upload.ttl.hour1'),     value: 60 * 60 },
      { label: t('chat.upload.ttl.hours24'),   value: 24 * 60 * 60 },
      { label: t('chat.upload.ttl.days3'),     value: 3 * 24 * 60 * 60 },
      { label: t('chat.upload.ttl.days30'),    value: 30 * 24 * 60 * 60 },
    ],
    [t]
  );
  
  // clears local grid + context for given item ids
  const clearSentPreviews = React.useCallback((ids: string[]) => {
    setItems(prev => {
      prev.forEach(it => {
        if (!ids.includes(it.id)) return;
        try { URL.revokeObjectURL(it.previewUrl); } catch {}
      });
      return prev.filter(it => !ids.includes(it.id));
    });
    ids.forEach(removeMediaItem);
  }, [removeMediaItem, setItems]);

 const itemsRef = React.useRef<SelItem[]>([]);
   React.useEffect(() => { itemsRef.current = items; }, [items]);


  // clears EVERYTHING in this screen + context
  const clearAllPreviews = React.useCallback(() => {
      // revoke all blob URLs we created for the grid
      setItems(prev => {
        prev.forEach(it => {
          try { it.previewUrl?.startsWith('blob:') && URL.revokeObjectURL(it.previewUrl); } catch {}
        });
        return [];
      });
      // clear the shared context in a single atomic update
      setMediaItems([]);                // <-- no per-item removals, no mediaItems dep
      injectedPreviewRef.current = null;
      setTtlAfterViewSec(-1);
    }, [setMediaItems]);
 

const goToThread = React.useCallback(() => {
  // 🔹 wipe selections before leaving
  clearAllPreviews();

  if (toUser?._id) {
    nav(`/chat/${toUser._id}`, {
      replace: true,
      state: {
        toUserId: toUser._id,
        toUserName: toUser.profilename,
        toUserPicture: toUser.picture,
      },
    });
  } else {
    nav('/chat', { replace: true });
  }
}, [nav, toUser?._id, toUser?.profilename, toUser?.picture, clearAllPreviews]);

  // cleanup temp preview file if we came from video editor
  React.useEffect(() => {
    const fn = locationState?.previewFilename;
    const isPreview = (locationState as any)?.isPreview;
    if (!fn || !isPreview) return;

    (async () => {
      try {
        await api.delete(`/videopreview-chat/${encodeURIComponent(fn)}`);
      } catch (e) {
        console.warn('Preview cleanup failed (non-fatal):', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guard
  React.useEffect(() => {
    if (!toUser && !previewFile) goToThread();   // allow page if preview is coming back from editor
  }, [toUser, previewFile, goToThread]);

  // Auto-open file picker
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (openPicker && inputRef.current) inputRef.current.click();
  }, [openPicker]);

  // Cleanup object URLs on unmount
  React.useEffect(() => {
    return () => {
      for (const it of items) {
        try { URL.revokeObjectURL(it.previewUrl); } catch {}
      }
    };
  }, [items]);

  const formatBytes = (n: number) => {
    if (!Number.isFinite(n)) return "";
    const units = ["B","KB","MB","GB","TB"];
    let i = 0; let v = n;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };

  // Picking
  const onPick = async (files: FileList | null) => {
    if (!files || !files.length) return;
    const next: SelItem[] = [];
    const nextCtx: { id: string; file: File }[] = [];
  
    for (const originalFile of Array.from(files)) {
      let file = originalFile;
  
      let kind = guessKind(file);   // 👈 use helper
      if (!kind) {
        console.warn('[upload] skipping file with unknown type', {
          name: file.name,
          type: file.type,
          size: file.size,
        });
        continue;
      }
  
      try {
        if (kind === 'video') {
          // 🔒 gate before we ever encrypt/upload
          await ensureAllowedVideo(file);
        } else if (kind === 'image') {
          // 🔄 normalize to JPEG so all browsers can decode it
          file = await normalizeImageToJpeg(file);
          // in case mime changed
          kind = 'image';
        }
  
        const id = `sel-${crypto.randomUUID?.() || Date.now() + '-' + Math.random()}`;
  
        nextCtx.push({ id, file });
  
        next.push({
          id,
          file,
          kind,
          previewUrl: URL.createObjectURL(file),
          status: 'ready',
        });
      } catch (e: any) {
        console.warn('[upload] rejected media:', e?.message || e);
      }
    }
  
    setItems(prev => [...prev, ...next]);
    if (nextCtx.length) {
      setMediaItems(prev => [...prev, ...nextCtx]);
    }
  };
  
  

  // Drag & drop
  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dt = e.dataTransfer;
    if (!dt?.files?.length) return;
    onPick(dt.files);
  };
  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Remove
  const removeItem = (id: string) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it) {
        try { URL.revokeObjectURL(it.previewUrl); } catch {}
      }
      return prev.filter((x) => x.id !== id);
    });
    removeMediaItem(id);
  };

  // inside ChatUploadMedia.tsx

const startBackgroundSend = React.useCallback(() => {
  if (!toUser || items.length === 0 || ttlAfterViewSec < 0) return;

  const batch = items.map(it => ({
    id: it.id,                   // temp id
    toUserId: toUser._id,
    file: it.file,
    ttlAfterViewSec,
  }));

  uploadQueue.enqueue(batch);

  // optional: clear previews now to free memory
  setItems([]);
  setMediaItems([]);    // clears context
  injectedPreviewRef.current = null;

  // 🚀 jump to thread immediately
  nav(`/chat/${toUser._id}`, {
    replace: true,
    state: {
      toUserId: toUser._id,
      toUserName: toUser.profilename,
      toUserPicture: toUser.picture,
      justEnqueued: batch.map(b => b.id), // temp ids for placeholders
    },
  });
}, [items, ttlAfterViewSec, toUser, nav, setMediaItems]);



  const disabled = !toUser || !e2eeReady || items.length === 0 || sending || ttlAfterViewSec < 0;


React.useEffect(() => {
    return () => { clearAllPreviews(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // inside ChatUploadMedia.tsx, near other refs
const didScrubRef = React.useRef(false);

// 🔸 1) On first mount, wipe context/local list unless we’re coming back with an edited preview
React.useEffect(() => {
  const comingFromEditor =
    !!locationState?.previewFile ||
    !!locationState?.editedUrl ||
    !!locationState?.previewFilename;

  if (!comingFromEditor) {
    // start clean
    setItems([]);
    setMediaItems([]);                 // clear shared context too
    injectedPreviewRef.current = null;
    didScrubRef.current = true;
  }
  // optional: drop state so a subsequent back doesn’t re-seed from it
  // nav('.', { replace: true, state: comingFromEditor ? locationState : {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// 🔸 2) In your build-from-context effect, bail if we intentionally scrubbed and there’s no editor preview
React.useEffect(() => {
  let cancelled = false;

  // If we scrubbed on entry and there's still no preview coming from editor, stay empty.
  if (didScrubRef.current && !previewFile) {
    return;
  }

  (async () => {
    const seeded: SelItem[] = [];
    for (const m of mediaItems) {
      if (!m.file) continue;
      const f = m.file;
      const kind = guessKind(f) || 'image';
      const existing = itemsRef.current.find(x => x.id === m.id);
      const previewUrl = existing?.previewUrl || URL.createObjectURL(f);

      seeded.push({
        id: m.id,
        file: f,
        kind,
        previewUrl,
        status: "ready",
      });
    }

    if (previewFile && (!previewFilename || injectedPreviewRef.current !== previewFilename)) {
      const id =
        editTargetId ||
        mediaItems.find((x) => x.file === previewFile)?.id ||
        `sel-${Date.now()}-${Math.random()}`;

      setMediaItems(prev => {
        const has = prev.some(p => p.id === id);
        return has ? prev : [...prev, { id, file: previewFile }];
      });
      if (previewFilename) injectedPreviewRef.current = previewFilename;

      const existing = itemsRef.current.find(x => x.id === id);
      const previewUrl = existing?.previewUrl || URL.createObjectURL(previewFile);
      const kind = guessKind(previewFile) || 'image';

      const edited: SelItem = { id, file: previewFile, kind, previewUrl, status: "ready" };

      const idx = seeded.findIndex((x) => x.id === id);
      if (idx === -1) seeded.push(edited);
      else seeded[idx] = edited;
    }

    if (!cancelled) {
      setItems(prev => {
        const map = new Map<string, SelItem>();
        for (const p of itemsRef.current) map.set(p.id, p);
        for (const s of seeded) map.set(s.id, s);
        return Array.from(map.values());
      });
    }
  })();

  return () => { cancelled = true; };
  // keep your current deps, plus previewFilename if not present
}, [mediaItems, previewFile, editTargetId, previewFilename, setMediaItems]);

  
  return (
    <div
      style={styles.wrap}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      <header style={styles.header}>
        <button onClick={goToThread} style={styles.backBtn} title={t('chat.upload.back')} aria-label={t('chat.upload.back')}>←</button>
        <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
  {t('chat.upload.headerTitle', {
    name: toUser?.profilename ?? t('chat.upload.genericUser'),
  })}
</div>
        <div />
      </header>
  
      {/* Picker bar */}
      <div style={styles.pickerBar}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={(e) => onPick(e.target.files)}
          style={{ display: "none" }}
        />
        <button
          onClick={() => inputRef.current?.click()}
          style={styles.pickBtn}
          disabled={!e2eeReady}
          title={
            !e2eeReady
              ? t('chat.upload.addButtonTitleInit')
              : t('chat.upload.addButtonTitleReady')
          }
        >
          {t('chat.upload.addButton')}
        </button>
  
        <select
  value={ttlAfterViewSec}
  onChange={(e) => setTtlAfterViewSec(parseInt(e.target.value, 10))}
  style={styles.ttlSelect}
  title={t('chat.upload.ttlTitle')}
>
  {ttlOptions.map(opt => (
    <option key={opt.value} value={opt.value}>
      {opt.label}
    </option>
  ))}
</select>
  
<button
  onClick={startBackgroundSend}
  style={{ ...styles.sendBtn, opacity: disabled ? 0.6 : 1 }}
  disabled={disabled}
>
  {t('chat.upload.sendCta', { count: items.length })}
</button>

      </div>
  
      {/* ===== EMPTY (single, full-width dropzone) ===== */}
      {!hasMedia && (
  <div style={styles.dropzoneWrap}>
    <div
  style={styles.dropzone}
  onClick={() => inputRef.current?.click()}
  role="button"
  tabIndex={0}
  onKeyDown={(e) =>
    (e.key === "Enter" || e.key === " ") && inputRef.current?.click()
  }
  title={t('chat.upload.dropTitle')}
>
  <div style={{ fontSize: 28, marginBottom: 10 }}>📷 🎬</div>
  <div style={{ fontSize: 15, color: "#cbd5e1" }}>
    {t('chat.upload.dropHelp')}
  </div>
</div>
  </div>
)}

  
      {/* ===== GRID (masonry via CSS columns) ===== */}
      {hasMedia && (
        <div style={styles.grid}>
          {items.map((it, idx) => {
            const isBusy = it.status === 'encrypting' || it.status === 'uploading';
            const isImage = it.kind === 'image';
  
            return (
              <div
   key={it.id}
   style={{ ...styles.box, cursor: "default" }}
   title={isImage ? t('chat.upload.kindImage') : t('chat.upload.kindVideo')}
   >
                {isImage ? (
                  <img
                    src={it.previewUrl}
                    alt=""
                    style={styles.media}
                    draggable={false}
                  />
                ) : (
                  <video
                    key={it.previewUrl}
                    src={it.previewUrl}
                    controls               // ✅ make videos playable
                    playsInline             // ✅ iOS inline playback
                    preload="metadata"
                    style={styles.media as React.CSSProperties}
                    data-grid-video="1"
                  />
                )}
  
                {/* Remove (top-right) */}
                <button
                  onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                  style={styles.removeBtn}
                  title={t('chat.upload.remove')}
                  aria-label={t('chat.upload.remove')}
                >
                  ✖
                </button>
  
                {/* Busy overlay */}
                {isBusy && (
                  <div style={styles.busyOverlay}>
                    <div style={styles.spinner} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
  );
  
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { minHeight: "100vh", background: "#0b0b0b", color: "#fff" },

  header: {
    display: "grid",
    gridTemplateColumns: "48px 1fr 48px",
    alignItems: "center",
    gap: 8,
    padding: "14px 16px",
    borderBottom: "1px solid #1f2937",
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    border: "none",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    cursor: "pointer",
  },

  pickerBar: {
    display: "flex",
    gap: 12,
    padding: "14px 16px",
    borderBottom: "1px solid #111827",
  },
  pickBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #374151",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
  },
  sendBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
  },

  // Masonry-style grid using CSS columns (matches presentation feel)
  grid: {
    padding: 16,
    columnWidth: 280,     // browser decides columns based on this target width
    columnGap: 16,
  },

  // Tile wrapper that flows inside columns
  box: {
    display: "inline-block",
    width: "100%",
    marginBottom: 16,
    position: "relative",
    cursor: "pointer",
  },

  // Media itself: natural aspect, no cropping
  media: {
    display: "block",
    width: "100%",
    height: "auto",
    objectFit: "contain",
    borderRadius: 12,     // nice corners; remove if you want square
    background: "#000",   // clean letterbox for videos & images
  },

  // Remove button in upper-right
  removeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "6px 8px",
    cursor: "pointer",
    fontSize: 12,
    backdropFilter: "blur(2px)",
  },

  // Busy overlay + spinner (minimal)
  busyOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "grid",
    placeItems: "center",
    borderRadius: 12,
  },
  spinner: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    animation: "spin 1s linear infinite",
  },

  // Empty state
  empty: {
    gridColumn: "1 / -1",
    padding: "12vh 0",
    textAlign: "center",
    color: "#9ca3af",
    border: "1px dashed #1f2937",
    borderRadius: 12,
    background: "rgba(255,255,255,0.02)",
    cursor: "pointer",
    userSelect: "none",
  },

  ttlSelect: {
    appearance: "none",
    WebkitAppearance: "none" as any,
    MozAppearance: "none" as any,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #374151",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
    minWidth: 220,
  },

  dropzoneWrap: {
    // centers the field horizontally, then nudges slightly left
    display: "flex",
    justifyContent: "center",
    padding: "8vh 16px",
  },
  
  dropzone: {
    // sized like a tidy input “field”
    width: "min(860px, 88vw)",
    minHeight: 180,
  
    // subtle mid/left placement: center, then shift left a bit
    transform: "translateX(-4%)",
  
    // field look
    background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.02))",
    border: "1px dashed #283142",
    borderRadius: 14,
    boxShadow: "0 10px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05)",
  
    // content
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#9fb0c0",
    cursor: "pointer",
    userSelect: "none",
    padding: "36px 18px",
  
    // nice focus/hover affordance even without :hover
    transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
  } as React.CSSProperties,
  
};

// Add once in global CSS:
// @keyframes spin { to { transform: rotate(360deg); } }

export default ChatUploadMedia;
